// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import { type WorkOrderKanban, useEvolvePatch, type WithOptional } from '@evolvemep/api-ts';
import { Flex, Text } from '@mantine/core';

import { CustomPropertiesPopover } from 'components/Mantine/CustomProperties/CustomPropertiesPopover';
import { KanbanCard } from 'components/Mantine/Kanban/KanbanCard';
import { EvolveLink } from 'components/Mantine/Navigation/EvolveLink';

type Props = {
  workOrder: WithOptional<WorkOrderKanban, 'sortOrder'>;
  index: number;
  draggable?: boolean;
};

export const WorkOrderKanbanCard = ({ workOrder: initialWorkOrder, index, draggable = true }: Props) => {
  const [workOrder, setWorkOrder] = useState(initialWorkOrder);

  const { apiCall: patchWorkOrderApiCall, loading: saving } = useEvolvePatch(`shop/workOrder/${workOrder.workOrderId}`);

  return (
    <KanbanCard
      index={index}
      id={workOrder.workOrderId}
      draggable={draggable}
      currentPage="Work Orders"
      cardDestination={`/work-orders/${workOrder.workOrderId}`}
      {...workOrder}
      workOrderName={workOrder.name}
      customStatusEntityName="Work Order"
      stateName={workOrder.workOrderStateName}
      statusId={workOrder.workOrderStatusId}
      editableStatus
      badges={
        <CustomPropertiesPopover
          size="lg"
          entity={workOrder}
          disabled={saving}
          customPropertyTypeName="Work Order"
          onUpdateCustomProperty={(customPropertyId, value) =>
            patchWorkOrderApiCall({
              customProperties: [{ customPropertyId, value }],
            }).then(({ customProperties }) =>
              setWorkOrder({
                ...workOrder,
                customProperties,
              }),
            )
          }
        />
      }
      onWorkOrderUpdated={(updatedWorkOrder) =>
        setWorkOrder((wo) => ({
          ...wo,
          ...updatedWorkOrder,
        }))
      }
      comment={
        workOrder.description ? (
          <Flex direction="column" gap={4}>
            {workOrder.description}
          </Flex>
        ) : null
      }
    >
      <EvolveLink className="kanban-work-order-link" to={`/work-orders/${workOrder.workOrderId}`} from="Work Orders">
        <Text fw={500} display="inline" lineClamp={3} title={workOrder.name}>
          {workOrder.name}
        </Text>
      </EvolveLink>
    </KanbanCard>
  );
};
